import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
} from '@mui/material';
import { insiderApi } from '../../services/api';

function TradeHistory() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadTrades();
  }, [page, rowsPerPage]);

  const loadTrades = async () => {
    try {
      setLoading(true);
      const response = await insiderApi.getTrades({ page: page + 1, per_page: rowsPerPage });
      setTrades(response.data.trades || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error loading trades:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Trade History
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Entry Price</TableCell>
                  <TableCell align="right">Exit Price</TableCell>
                  <TableCell align="right">P/L</TableCell>
                  <TableCell align="right">Return %</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trades.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" py={3}>
                        No trades yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  trades.map((trade) => {
                    const isClosed = trade.status === 'closed';
                    const pl = isClosed ? (trade.realized_pl || 0) : (trade.unrealized_pl || 0);
                    const returnPct = isClosed && trade.return_pct != null ? trade.return_pct * 100 : 0;
                    return (
                      <TableRow key={trade.trade_id}>
                        <TableCell>{formatDate(trade.entry_time)}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold">{trade.symbol}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={trade.side}
                            size="small"
                            sx={{
                              bgcolor: trade.side === 'buy' ? '#e3f2fd' : '#ffebee',
                              color: trade.side === 'buy' ? '#1565c0' : '#c62828',
                              fontWeight: 'bold',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{trade.quantity}</TableCell>
                        <TableCell align="right">${trade.entry_price?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell align="right">
                          {isClosed && trade.exit_price != null ? `$${trade.exit_price.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{ color: pl >= 0 ? '#2e7d32' : '#d32f2f' }}
                          >
                            {pl >= 0 ? '+' : ''}${pl?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {isClosed ? (
                            <Chip
                              label={`${returnPct >= 0 ? '+' : ''}${returnPct.toFixed(2)}%`}
                              size="small"
                              sx={{
                                bgcolor: returnPct >= 0 ? '#e8f5e9' : '#ffebee',
                                color: returnPct >= 0 ? '#2e7d32' : '#d32f2f',
                                fontWeight: 'bold',
                              }}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={trade.status}
                            size="small"
                            color={trade.status === 'open' ? 'primary' : 'default'}
                            variant={trade.status === 'open' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}

export default TradeHistory;
